"use client";
import { useEffect, useState } from "react";
import {
  query,
  collection,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase.config";
import MenuIcon from "@mui/icons-material/Menu";
import "../../styles/df-messenger-custom.css";
import Sidebar from "./sidebar";
import ChatBox from "./chatBox";
import InstantChatModal from "./instantChatModal";
import { useTheme } from "../../context/ThemeContext";
import toast, { ToastBar, Toaster } from "react-hot-toast";

// Theme Toggle Component
const ThemeToggle = ({ className = "" }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-2xl font-medium transition-all duration-200 backdrop-blur-sm ${className}`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
    >
      {theme === "dark" ? (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <span className="hidden sm:inline">Light</span>
        </>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
          <span className="hidden sm:inline">Dark</span>
        </>
      )}
    </button>
  );
};

export default function DialogflowMessenger() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [progressData, setProgressData] = useState([]);
  const [showInstantChat, setShowInstantChat] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [day, setDay] = useState(null);
  const { theme } = useTheme();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const day = localStorage.getItem("courseDay");
    setDay(day);
  }, []);

  window.addEventListener("courseDayUpdated", () => {
    const newDay = localStorage.getItem("courseDay");
    setDay(newDay);
  });

  // Apply theme to Dialogflow messenger when theme changes
  useEffect(() => {
    const applyThemeToDialogflow = () => {
      const dfMessenger = document.querySelector("df-messenger");
      if (dfMessenger) {
        if (theme === "dark") {
          dfMessenger.setAttribute("data-theme", "dark");
        } else {
          dfMessenger.removeAttribute("data-theme");
        }
      }
    };

    // Apply theme with retry logic for shadow DOM
    const timeouts = [100, 500, 1000, 2000, 3000];
    const timeoutIds = timeouts.map((delay) =>
      setTimeout(applyThemeToDialogflow, delay)
    );

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [theme]);

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    const uid = authUser?.uid;

    if (!uid) {
      console.warn("No user found in localStorage.");
      return;
    }

    const progressQuery = query(
      collection(db, "daily_progress"),
      where("userId", "==", uid),
      orderBy("courseDay", "asc")
    );

    const progressUnsubscribe = onSnapshot(
      progressQuery,
      (querySnapshot) => {
        if (!querySnapshot.empty) {
          const userDocs = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log("Real-time progress data:", userDocs);
          setProgressData(userDocs);
        } else {
          console.log("No daily_progress document found for this user.");
          setProgressData([]);
        }
      },
      (error) => {
        console.error("Real-time progress data error:", error);
      }
    );

    return () => {
      progressUnsubscribe();
    };
  }, []);

  return (
    <>
      <div
        className={`flex h-screen w-full overflow-hidden ${theme === "dark" ? "bg-[#0F1115]" : "bg-[#FAFBFC]"
          } text-white z-2`}
      >
        <Sidebar
          progressData={progressData}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleSidebar={toggleSidebar}
          courseDay={day}
          assignments={assignments}
          setAssignments={setAssignments}
        />

        {/* Main Content */}
        <div className="flex flex-1 flex-col z-20">
          {/* Header Container */}
          <header
            className={`dashboard-header ${theme === "dark"
              ? "dashboard-header-dark"
              : "dashboard-header-light"
              }`}
          >
            {/* Content wrapper */}
            <div className="flex w-full items-center justify-between">
              {/* Sidebar toggle - mobile only */}
              <div className="md:hidden">
                <button
                  className={`dashboard-mobile-button ${theme === "dark"
                    ? "dashboard-mobile-button-dark"
                    : "dashboard-mobile-button-light"
                    }`}
                  onClick={toggleSidebar}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`w-5 h-5 ${theme === "dark" ? "text-gray-200" : "text-gray-700"
                      }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Center Title */}
              <div className="flex-1 flex justify-start items-center">
                <h1
                  className={`text-xl sm:text-2xl md:text-2xl  ${theme === "dark" ? "text-white" : "text-gray-800"
                    }`}
                >
                  Ascendra
                </h1>
                <div
                  className={`hidden lg:block ml-4 text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                >
                  You’re using the beta version of the app. If you encounter any
                  issues, please report them using this{" "}
                  <a
                    href="https://forms.gle/d1BTw7kq1VduBLLXA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-emerald-500"
                  >
                    form
                  </a>{" "}
                  or contact contact@ascendra.me
                </div>
              </div>

              {/* Desktop right actions */}
              <div className="hidden md:flex items-center gap-3">
                <div
                  className={`dashboard-day-counter flex items-center gap-3 w-full ${theme === "dark"
                    ? "dashboard-day-counter-dark text-white"
                    : "dashboard-day-counter-light text-gray-800"
                    }`}
                >
                  <div className="flex items-center w-full gap-4">
                    <progress
                      value={day ?? 0}
                      max={42}
                      className={`flex-grow h-4 rounded-xl overflow-hidden appearance-none transition-all duration-300
        ${theme === "dark"
                          ? "[&::-webkit-progress-bar]:bg-gray-700 [&::-webkit-progress-value]:bg-green-400"
                          : "[&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-green-600"
                        }
      `}
                    />
                    <span className="whitespace-nowrap text-sm">{`Day ${day ?? 0
                      } of 42`}</span>
                  </div>
                </div>

                {/* Theme Toggle */}
                <ThemeToggle
                  className={`dashboard-theme-toggle ${theme === "dark"
                    ? "dashboard-theme-toggle-dark"
                    : "dashboard-theme-toggle-light"
                    }`}
                />
              </div>

              {/* Mobile right actions */}
              <div className="flex md:hidden items-center gap-2">
                <ThemeToggle
                  className={`dashboard-theme-toggle-mobile ${theme === "dark"
                    ? "dashboard-theme-toggle-mobile-dark"
                    : "dashboard-theme-toggle-mobile-light"
                    }`}
                />
              </div>
            </div>
          </header>

          <ChatBox setAssignments={setAssignments} assignments={assignments} />
        </div>
      </div>

      {showInstantChat && (
        <InstantChatModal
          setShowInstantChat={setShowInstantChat}
          showInstantChat={showInstantChat}
          setAssignments={setAssignments}
          assignments={assignments}
        />
      )}
    </>
  );
}
